import { FocusMonitor } from '@angular/cdk/a11y';
import { Direction, Directionality } from '@angular/cdk/bidi';
import { DOWN_ARROW, ENTER, ESCAPE, SPACE, TAB, UP_ARROW } from '@angular/cdk/keycodes';
import {
  CdkConnectedOverlay,
  CdkOverlayOrigin,
  ConnectedOverlayPositionChange,
  ConnectionPositionPair
} from '@angular/cdk/overlay';
import { Platform } from '@angular/cdk/platform';
import {
  AfterContentInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChildren,
  ElementRef,
  EventEmitter,
  forwardRef,
  Host,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  Optional,
  Output,
  QueryList,
  Renderer2,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { BehaviorSubject, combineLatest, fromEvent, merge, of as observableOf } from 'rxjs';
import { distinctUntilChanged, map, startWith, switchMap, takeUntil, withLatestFrom } from 'rxjs/operators';

import { slideMotion } from 'mwc/core/animation';
import { MwcConfigKey, MwcConfigService, WithConfig } from 'mwc/core/config';
import { MwcFormNoStatusService, MwcFormStatusService } from 'mwc/core/form';
import { MwcNoAnimationDirective } from 'mwc/core/no-animation';
import { getPlacementName, POSITION_MAP, POSITION_TYPE } from 'mwc/core/overlay';
import { cancelRequestAnimationFrame, reqAnimFrame } from 'mwc/core/polyfill';
import { MwcDestroyService } from 'mwc/core/services';
import {
  BooleanInput,
  NgClassInterface,
  MwcSafeAny,
  MwcStatus,
  MwcValidateStatus,
  OnChangeType,
  OnTouchedType
} from 'mwc/core/types';
import { getStatusClassNames, InputBoolean, isNotNil } from 'mwc/core/util';

import { MwcOptionGroupComponent } from './option-group.component';
import { MwcOptionComponent } from './option.component';
import { MwcSelectTopControlComponent } from './select-top-control.component';
import { MwcSelectSearchComponent } from './select-search.component';
import {
  MwcFilterOptionType,
  MwcSelectItemInterface,
  MwcSelectModeType,
  MwcSelectOptionInterface,
  MwcSelectPlacementType
} from './select.types';

const defaultFilterOption: MwcFilterOptionType = (searchValue: string, item: MwcSelectItemInterface): boolean => {
  if (item && item.mwcLabel) {
    return item.mwcLabel.toString().toLowerCase().indexOf(searchValue.toLowerCase()) > -1;
  } else {
    return false;
  }
};

const NZ_CONFIG_MODULE_NAME: MwcConfigKey = 'select';

export type MwcSelectSizeType = 'large' | 'default' | 'small';

@Component({
  selector: 'mwc-select',
  exportAs: 'mwcSelect',
  preserveWhitespaces: false,
  providers: [
    MwcDestroyService,
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MwcSelectComponent),
      multi: true
    }
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  animations: [slideMotion],
  template: `
    <mwc-select-top-control
      cdkOverlayOrigin
      #origin="cdkOverlayOrigin"
      [mwcId]="mwcId"
      [open]="mwcOpen"
      [disabled]="mwcDisabled"
      [mode]="mwcMode"
      [@.disabled]="!!noAnimation?.mwcNoAnimation"
      [mwcNoAnimation]="noAnimation?.mwcNoAnimation"
      [maxTagPlaceholder]="mwcMaxTagPlaceholder"
      [removeIcon]="mwcRemoveIcon"
      [placeHolder]="mwcPlaceHolder"
      [maxTagCount]="mwcMaxTagCount"
      [customTemplate]="mwcCustomTemplate"
      [tokenSeparators]="mwcTokenSeparators"
      [showSearch]="mwcShowSearch"
      [autofocus]="mwcAutoFocus"
      [listOfTopItem]="listOfTopItem"
      [mwcSelectSearchComponent]="mwcSelectSearchComponent"
      (inputValueChange)="onInputValueChange($event)"
      (tokenize)="onTokenSeparate($event)"
      (deleteItem)="onItemDelete($event)"
      (keydown)="onKeyDown($event)"
    ></mwc-select-top-control>
    <mwc-select-arrow
      *ngIf="mwcShowArrow || (hasFeedback && !!status)"
      [showArrow]="mwcShowArrow"
      [loading]="mwcLoading"
      [search]="mwcOpen && mwcShowSearch"
      [suffixIcon]="mwcSuffixIcon"
      [feedbackIcon]="feedbackIconTpl"
    >
      <ng-template #feedbackIconTpl>
        <mwc-form-item-feedback-icon *ngIf="hasFeedback && !!status" [status]="status"></mwc-form-item-feedback-icon>
      </ng-template>
    </mwc-select-arrow>

    <mwc-select-clear
      *ngIf="mwcAllowClear && !mwcDisabled && listOfValue.length"
      [clearIcon]="mwcClearIcon"
      (clear)="onClearSelection()"
    ></mwc-select-clear>
    <ng-template
      cdkConnectedOverlay
      mwcConnectedOverlay
      [cdkConnectedOverlayHasBackdrop]="mwcBackdrop"
      [cdkConnectedOverlayMinWidth]="$any(mwcDropdownMatchSelectWidth ? null : triggerWidth)"
      [cdkConnectedOverlayWidth]="$any(mwcDropdownMatchSelectWidth ? triggerWidth : null)"
      [cdkConnectedOverlayOrigin]="origin"
      [cdkConnectedOverlayTransformOriginOn]="'.mwc-select-dropdown'"
      [cdkConnectedOverlayPanelClass]="mwcDropdownClassName!"
      [cdkConnectedOverlayOpen]="mwcOpen"
      [cdkConnectedOverlayPositions]="positions"
      (overlayOutsideClick)="onClickOutside($event)"
      (detach)="setOpenState(false)"
      (positionChange)="onPositionChange($event)"
    >
      <mwc-option-container
        [ngStyle]="mwcDropdownStyle"
        [itemSize]="mwcOptionHeightPx"
        [maxItemLength]="mwcOptionOverflowSize"
        [matchWidth]="mwcDropdownMatchSelectWidth"
        [class.mwc-select-dropdown-placement-bottomLeft]="dropDownPosition === 'bottomLeft'"
        [class.mwc-select-dropdown-placement-topLeft]="dropDownPosition === 'topLeft'"
        [class.mwc-select-dropdown-placement-bottomRight]="dropDownPosition === 'bottomRight'"
        [class.mwc-select-dropdown-placement-topRight]="dropDownPosition === 'topRight'"
        [@slideMotion]="'enter'"
        [@.disabled]="!!noAnimation?.mwcNoAnimation"
        [mwcNoAnimation]="noAnimation?.mwcNoAnimation"
        [listOfContainerItem]="listOfContainerItem"
        [menuItemSelectedIcon]="mwcMenuItemSelectedIcon"
        [notFoundContent]="mwcNotFoundContent"
        [activatedValue]="activatedValue"
        [listOfSelectedValue]="listOfValue"
        [dropdownRender]="mwcDropdownRender"
        [compareWith]="compareWith"
        [mode]="mwcMode"
        (keydown)="onKeyDown($event)"
        (itemClick)="onItemClick($event)"
        (scrollToBottom)="mwcScrollToBottom.emit()"
      >
        <mwc-select-search *ngIf="(mwcShowSearch || mwcMode !== 'default') && !mwcDisabled"
          [mwcId]="mwcId"
          [disabled]="mwcDisabled"
          [value]="mwcSelectTopControlComponent.inputValue!"
          [autofocus]="mwcAutoFocus"
          [showInput]="true"
          [mirrorSync]="false"
          [focusTrigger]="mwcOpen"
          (isComposingChange)="mwcSelectTopControlComponent.isComposingChange($event)"
          (valueChange)="mwcSelectTopControlComponent.onInputValueChange($event)"
        ></mwc-select-search>
      </mwc-option-container>
    </ng-template>
  `,
  host: {
    class: 'mwc-select',
    '[class.mwc-select-in-form-item]': '!!mwcFormStatusService',
    '[class.mwc-select-lg]': 'mwcSize === "large"',
    '[class.mwc-select-sm]': 'mwcSize === "small"',
    '[class.mwc-select-show-arrow]': `mwcShowArrow`,
    '[class.mwc-select-disabled]': 'mwcDisabled',
    '[class.mwc-select-show-search]': `(mwcShowSearch || mwcMode !== 'default') && !mwcDisabled`,
    '[class.mwc-select-allow-clear]': 'mwcAllowClear',
    '[class.mwc-select-borderless]': 'mwcBorderless',
    '[class.mwc-select-open]': 'mwcOpen',
    '[class.mwc-select-focused]': 'mwcOpen || focused',
    '[class.mwc-select-single]': `mwcMode === 'default'`,
    '[class.mwc-select-multiple]': `mwcMode !== 'default'`,
    '[class.mwc-select-rtl]': `dir === 'rtl'`
  }
})
export class MwcSelectComponent implements ControlValueAccessor, OnInit, AfterContentInit, OnChanges, OnDestroy {
  readonly _mwcModuleName: MwcConfigKey = NZ_CONFIG_MODULE_NAME;

  static ngAcceptInputType_mwcAllowClear: BooleanInput;
  static ngAcceptInputType_mwcBorderless: BooleanInput;
  static ngAcceptInputType_mwcShowSearch: BooleanInput;
  static ngAcceptInputType_mwcLoading: BooleanInput;
  static ngAcceptInputType_mwcAutoFocus: BooleanInput;
  static ngAcceptInputType_mwcAutoClearSearchValue: BooleanInput;
  static ngAcceptInputType_mwcServerSearch: BooleanInput;
  static ngAcceptInputType_mwcDisabled: BooleanInput;
  static ngAcceptInputType_mwcOpen: BooleanInput;

  @Input() mwcId: string | null = null;
  @Input() mwcSize: MwcSelectSizeType = 'default';
  @Input() mwcStatus: MwcStatus = '';
  @Input() mwcOptionHeightPx = 44;
  @Input() mwcOptionOverflowSize = 8;
  @Input() mwcDropdownClassName: string[] | string | null = null;
  @Input() mwcDropdownMatchSelectWidth = true;
  @Input() mwcDropdownStyle: { [key: string]: string } | null = null;
  @Input() mwcNotFoundContent: string | TemplateRef<MwcSafeAny> | undefined = undefined;
  @Input() mwcPlaceHolder: string | TemplateRef<MwcSafeAny> | null = null;
  @Input() mwcPlacement: MwcSelectPlacementType | null = null;
  @Input() mwcMaxTagCount = Infinity;
  @Input() mwcDropdownRender: TemplateRef<MwcSafeAny> | null = null;
  @Input() mwcCustomTemplate: TemplateRef<{ $implicit: MwcSelectItemInterface }> | null = null;
  @Input()
  @WithConfig<TemplateRef<MwcSafeAny> | string | null>()
  mwcSuffixIcon: TemplateRef<MwcSafeAny> | string | null = null;
  @Input() mwcClearIcon: TemplateRef<MwcSafeAny> | null = null;
  @Input() mwcRemoveIcon: TemplateRef<MwcSafeAny> | null = null;
  @Input() mwcMenuItemSelectedIcon: TemplateRef<MwcSafeAny> | null = null;
  @Input() mwcTokenSeparators: string[] = [];
  @Input() mwcMaxTagPlaceholder: TemplateRef<{ $implicit: MwcSafeAny[] }> | null = null;
  @Input() mwcMaxMultipleCount = Infinity;
  @Input() mwcMode: MwcSelectModeType = 'default';
  @Input() mwcFilterOption: MwcFilterOptionType = defaultFilterOption;
  @Input() compareWith: (o1: MwcSafeAny, o2: MwcSafeAny) => boolean = (o1: MwcSafeAny, o2: MwcSafeAny) => o1 === o2;
  @Input() @InputBoolean() mwcAllowClear = false;
  @Input() @WithConfig<boolean>() @InputBoolean() mwcBorderless = false;
  @Input() @InputBoolean() mwcShowSearch = false;
  @Input() @InputBoolean() mwcLoading = false;
  @Input() @InputBoolean() mwcAutoFocus = false;
  @Input() @InputBoolean() mwcAutoClearSearchValue = true;
  @Input() @InputBoolean() mwcServerSearch = false;
  @Input() @InputBoolean() mwcDisabled = false;
  @Input() @InputBoolean() mwcOpen = false;
  @Input() @InputBoolean() mwcSelectOnTab = false;
  @Input() @WithConfig<boolean>() @InputBoolean() mwcBackdrop = false;
  @Input() mwcOptions: MwcSelectOptionInterface[] = [];

  @Input()
  set mwcShowArrow(value: boolean) {
    this._mwcShowArrow = value;
  }
  get mwcShowArrow(): boolean {
    return this._mwcShowArrow === undefined ? this.mwcMode === 'default' : this._mwcShowArrow;
  }

  @Output() readonly mwcOnSearch = new EventEmitter<string>();
  @Output() readonly mwcScrollToBottom = new EventEmitter<void>();
  @Output() readonly mwcOpenChange = new EventEmitter<boolean>();
  @Output() readonly mwcBlur = new EventEmitter<void>();
  @Output() readonly mwcFocus = new EventEmitter<void>();
  @ViewChild(CdkOverlayOrigin, { static: true, read: ElementRef }) originElement!: ElementRef;
  @ViewChild(CdkConnectedOverlay, { static: true }) cdkConnectedOverlay!: CdkConnectedOverlay;
  @ViewChild(MwcSelectTopControlComponent, { static: true })
  mwcSelectTopControlComponent!: MwcSelectTopControlComponent;
  @ContentChildren(MwcOptionComponent, { descendants: true }) listOfMwcOptionComponent!: QueryList<MwcOptionComponent>;
  @ContentChildren(MwcOptionGroupComponent, { descendants: true })
  listOfMwcOptionGroupComponent!: QueryList<MwcOptionGroupComponent>;
  @ViewChild(MwcOptionGroupComponent, { static: true, read: ElementRef }) mwcOptionGroupComponentElement!: ElementRef;
  @ViewChild(MwcSelectTopControlComponent, { static: true, read: ElementRef })
  mwcSelectTopControlComponentElement!: ElementRef;
  @ViewChild(MwcSelectSearchComponent) mwcSelectSearchComponent!: MwcSelectSearchComponent;
  private listOfValue$ = new BehaviorSubject<MwcSafeAny[]>([]);
  private listOfTemplateItem$ = new BehaviorSubject<MwcSelectItemInterface[]>([]);
  private listOfTagAndTemplateItem: MwcSelectItemInterface[] = [];
  private searchValue: string = '';
  private isReactiveDriven = false;
  private value: MwcSafeAny | MwcSafeAny[];
  private _mwcShowArrow: boolean | undefined;
  private requestId: number = -1;
  private isMwcDisableFirstChange: boolean = true;
  onChange: OnChangeType = () => {};
  onTouched: OnTouchedType = () => {};
  dropDownPosition: MwcSelectPlacementType = 'bottomLeft';
  triggerWidth: number | null = null;
  listOfContainerItem: MwcSelectItemInterface[] = [];
  listOfTopItem: MwcSelectItemInterface[] = [];
  activatedValue: MwcSafeAny | null = null;
  listOfValue: MwcSafeAny[] = [];
  focused = false;
  dir: Direction = 'ltr';
  positions: ConnectionPositionPair[] = [];
  inputValue: string | null = null;
  // status
  prefixCls: string = 'mwc-select';
  statusCls: NgClassInterface = {};
  status: MwcValidateStatus = '';
  hasFeedback: boolean = false;

  generateTagItem(value: string): MwcSelectItemInterface {
    return {
      mwcValue: value,
      mwcLabel: value,
      type: 'item'
    };
  }

  onItemClick(value: MwcSafeAny): void {
    this.activatedValue = value;
    if (this.mwcMode === 'default') {
      if (this.listOfValue.length === 0 || !this.compareWith(this.listOfValue[0], value)) {
        this.updateListOfValue([value]);
      }
      this.setOpenState(false);
    } else {
      const targetIndex = this.listOfValue.findIndex(o => this.compareWith(o, value));
      if (targetIndex !== -1) {
        const listOfValueAfterRemoved = this.listOfValue.filter((_, i) => i !== targetIndex);
        this.updateListOfValue(listOfValueAfterRemoved);
      } else if (this.listOfValue.length < this.mwcMaxMultipleCount) {
        const listOfValueAfterAdded = [...this.listOfValue, value];
        this.updateListOfValue(listOfValueAfterAdded);
      }
      this.focus();
      if (this.mwcAutoClearSearchValue) {
        this.clearInput();
      }
    }
  }

  onItemDelete(item: MwcSelectItemInterface): void {
    const listOfSelectedValue = this.listOfValue.filter(v => !this.compareWith(v, item.mwcValue));
    this.updateListOfValue(listOfSelectedValue);
    this.clearInput();
  }

  updateListOfContainerItem(): void {
    let listOfContainerItem = this.listOfTagAndTemplateItem
      .filter(item => !item.mwcHide)
      .filter(item => {
        if (!this.mwcServerSearch && this.searchValue) {
          return this.mwcFilterOption(this.searchValue, item);
        } else {
          return true;
        }
      });
    if (this.mwcMode === 'tags' && this.searchValue) {
      const matchedItem = this.listOfTagAndTemplateItem.find(item => item.mwcLabel === this.searchValue);
      if (!matchedItem) {
        const tagItem = this.generateTagItem(this.searchValue);
        listOfContainerItem = [tagItem, ...listOfContainerItem];
        this.activatedValue = tagItem.mwcValue;
      } else {
        this.activatedValue = matchedItem.mwcValue;
      }
    }
    const activatedItem =
      listOfContainerItem.find(item => item.mwcLabel === this.searchValue) ||
      listOfContainerItem.find(item => this.compareWith(item.mwcValue, this.activatedValue)) ||
      listOfContainerItem.find(item => this.compareWith(item.mwcValue, this.listOfValue[0])) ||
      listOfContainerItem[0];
    this.activatedValue = (activatedItem && activatedItem.mwcValue) || null;
    let listOfGroupLabel: Array<string | number | TemplateRef<MwcSafeAny> | null> = [];
    if (this.isReactiveDriven) {
      listOfGroupLabel = [...new Set(this.mwcOptions.filter(o => o.groupLabel).map(o => o.groupLabel!))];
    } else {
      if (this.listOfMwcOptionGroupComponent) {
        listOfGroupLabel = this.listOfMwcOptionGroupComponent.map(o => o.mwcLabel);
      }
    }
    /** insert group item **/
    listOfGroupLabel.forEach(label => {
      const index = listOfContainerItem.findIndex(item => label === item.groupLabel);
      if (index > -1) {
        const groupItem = { groupLabel: label, type: 'group', key: label } as MwcSelectItemInterface;
        listOfContainerItem.splice(index, 0, groupItem);
      }
    });
    this.listOfContainerItem = [...listOfContainerItem];
    this.updateCdkConnectedOverlayPositions();
  }

  clearInput(): void {
    this.mwcSelectTopControlComponent.clearInputValue();
  }

  updateListOfValue(listOfValue: MwcSafeAny[]): void {
    const covertListToModel = (list: MwcSafeAny[], mode: MwcSelectModeType): MwcSafeAny[] | MwcSafeAny => {
      if (mode === 'default') {
        if (list.length > 0) {
          return list[0];
        } else {
          return null;
        }
      } else {
        return list;
      }
    };
    const model = covertListToModel(listOfValue, this.mwcMode);
    if (this.value !== model) {
      this.listOfValue = listOfValue;
      this.listOfValue$.next(listOfValue);
      this.value = model;
      this.onChange(this.value);
    }
  }

  onTokenSeparate(listOfLabel: string[]): void {
    const listOfMatchedValue = this.listOfTagAndTemplateItem
      .filter(item => listOfLabel.findIndex(label => label === item.mwcLabel) !== -1)
      .map(item => item.mwcValue)
      .filter(item => this.listOfValue.findIndex(v => this.compareWith(v, item)) === -1);
    if (this.mwcMode === 'multiple') {
      this.updateListOfValue([...this.listOfValue, ...listOfMatchedValue]);
    } else if (this.mwcMode === 'tags') {
      const listOfUnMatchedLabel = listOfLabel.filter(
        label => this.listOfTagAndTemplateItem.findIndex(item => item.mwcLabel === label) === -1
      );
      this.updateListOfValue([...this.listOfValue, ...listOfMatchedValue, ...listOfUnMatchedLabel]);
    }
    this.clearInput();
  }

  onKeyDown(e: KeyboardEvent): void {
    if (this.mwcDisabled) {
      return;
    }
    const listOfFilteredOptionNotDisabled = this.listOfContainerItem
      .filter(item => item.type === 'item')
      .filter(item => !item.mwcDisabled);
    const activatedIndex = listOfFilteredOptionNotDisabled.findIndex(item =>
      this.compareWith(item.mwcValue, this.activatedValue)
    );
    switch (e.keyCode) {
      case UP_ARROW:
        e.preventDefault();
        if (this.mwcOpen && listOfFilteredOptionNotDisabled.length > 0) {
          const preIndex = activatedIndex > 0 ? activatedIndex - 1 : listOfFilteredOptionNotDisabled.length - 1;
          this.activatedValue = listOfFilteredOptionNotDisabled[preIndex].mwcValue;
        }
        break;
      case DOWN_ARROW:
        e.preventDefault();
        if (this.mwcOpen && listOfFilteredOptionNotDisabled.length > 0) {
          const nextIndex = activatedIndex < listOfFilteredOptionNotDisabled.length - 1 ? activatedIndex + 1 : 0;
          this.activatedValue = listOfFilteredOptionNotDisabled[nextIndex].mwcValue;
        } else {
          this.setOpenState(true);
        }
        break;
      case ENTER:
        e.preventDefault();
        if (this.mwcOpen) {
          if (isNotNil(this.activatedValue) && activatedIndex !== -1) {
            this.onItemClick(this.activatedValue);
          }
        } else {
          this.setOpenState(true);
        }
        break;
      case SPACE:
        if (!this.mwcOpen) {
          this.setOpenState(true);
          e.preventDefault();
        }
        break;
      case TAB:
        if (this.mwcSelectOnTab) {
          if (this.mwcOpen) {
            e.preventDefault();
            if (isNotNil(this.activatedValue)) {
              this.onItemClick(this.activatedValue);
            }
          }
        } else {
          this.setOpenState(false);
        }
        break;
      case ESCAPE:
        /**
         * Skip the ESCAPE processing, it will be handled in {@link onOverlayKeyDown}.
         */
        break;
      default:
        if (!this.mwcOpen) {
          this.setOpenState(true);
        }
    }
  }

  setOpenState(value: boolean): void {
    if (this.mwcOpen !== value) {
      this.mwcOpen = value;
      this.mwcOpenChange.emit(value);
      this.onOpenChange();
      this.cdr.markForCheck();
    }
  }

  onOpenChange(): void {
    this.updateCdkConnectedOverlayStatus();
    this.clearInput();
  }

  onInputValueChange(value: string): void {
    this.searchValue = value;
    this.updateListOfContainerItem();
    this.mwcOnSearch.emit(value);
    this.updateCdkConnectedOverlayPositions();
  }

  onClearSelection(): void {
    this.updateListOfValue([]);
  }

  onClickOutside(event: MouseEvent): void {
    if (!this.host.nativeElement.contains(event.target as HTMLElement)) {
      this.setOpenState(false);
    }
  }

  focus(): void {
    this.mwcSelectTopControlComponent.focus();
  }

  blur(): void {
    this.mwcSelectTopControlComponent.blur();
  }

  onPositionChange(position: ConnectedOverlayPositionChange): void {
    const placement = getPlacementName(position);
    this.dropDownPosition = placement as MwcSelectPlacementType;
  }

  updateCdkConnectedOverlayStatus(): void {
    if (this.platform.isBrowser && this.originElement.nativeElement) {
      const triggerWidth = this.triggerWidth;
      cancelRequestAnimationFrame(this.requestId);
      this.requestId = reqAnimFrame(() => {
        // Blink triggers style and layout pipelines anytime the `getBoundingClientRect()` is called, which may cause a
        // frame drop. That's why it's scheduled through the `requestAnimationFrame` to unload the composite thread.
        this.triggerWidth = this.originElement.nativeElement.getBoundingClientRect().width;
        if (triggerWidth !== this.triggerWidth) {
          // The `requestAnimationFrame` will trigger change detection, but we're inside an `OnPush` component which won't have
          // the `ChecksEnabled` state. Calling `markForCheck()` will allow Angular to run the change detection from the root component
          // down to the `mwc-select`. But we'll trigger only local change detection if the `triggerWidth` has been changed.
          this.cdr.detectChanges();
        }
      });
    }
  }

  updateCdkConnectedOverlayPositions(): void {
    reqAnimFrame(() => {
      this.cdkConnectedOverlay?.overlayRef?.updatePosition();
    });
  }

  constructor(
    private ngZone: NgZone,
    private destroy$: MwcDestroyService,
    public mwcConfigService: MwcConfigService,
    private cdr: ChangeDetectorRef,
    private host: ElementRef<HTMLElement>,
    private renderer: Renderer2,
    private platform: Platform,
    private focusMonitor: FocusMonitor,
    @Optional() private directionality: Directionality,
    @Host() @Optional() public noAnimation?: MwcNoAnimationDirective,
    @Optional() public mwcFormStatusService?: MwcFormStatusService,
    @Optional() private mwcFormNoStatusService?: MwcFormNoStatusService
  ) {}

  writeValue(modelValue: MwcSafeAny | MwcSafeAny[]): void {
    /** https://github.com/angular/angular/issues/14988 **/
    if (this.value !== modelValue) {
      this.value = modelValue;
      const covertModelToList = (model: MwcSafeAny[] | MwcSafeAny, mode: MwcSelectModeType): MwcSafeAny[] => {
        if (model === null || model === undefined) {
          return [];
        } else if (mode === 'default') {
          return [model];
        } else {
          return model;
        }
      };
      const listOfValue = covertModelToList(modelValue, this.mwcMode);
      this.listOfValue = listOfValue;
      this.listOfValue$.next(listOfValue);
      this.cdr.markForCheck();
    }
  }

  registerOnChange(fn: OnChangeType): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: OnTouchedType): void {
    this.onTouched = fn;
  }

  setDisabledState(disabled: boolean): void {
    this.mwcDisabled = (this.isMwcDisableFirstChange && this.mwcDisabled) || disabled;
    this.isMwcDisableFirstChange = false;
    if (this.mwcDisabled) {
      this.setOpenState(false);
    }
    this.cdr.markForCheck();
  }

  ngOnChanges(changes: SimpleChanges): void {
    const { mwcOpen, mwcDisabled, mwcOptions, mwcStatus, mwcPlacement } = changes;
    if (mwcOpen) {
      this.onOpenChange();
    }
    if (mwcDisabled && this.mwcDisabled) {
      this.setOpenState(false);
    }
    if (mwcOptions) {
      this.isReactiveDriven = true;
      const listOfOptions = this.mwcOptions || [];
      const listOfTransformedItem = listOfOptions.map(item => {
        return {
          template: item.label instanceof TemplateRef ? item.label : null,
          mwcLabel: typeof item.label === 'string' || typeof item.label === 'number' ? item.label : null,
          mwcValue: item.value,
          mwcDisabled: item.disabled || false,
          mwcHide: item.hide || false,
          mwcCustomContent: item.label instanceof TemplateRef,
          groupLabel: item.groupLabel || null,
          type: 'item',
          key: item.value
        };
      });
      this.listOfTemplateItem$.next(listOfTransformedItem);
    }
    if (mwcStatus) {
      this.setStatusStyles(this.mwcStatus, this.hasFeedback);
    }
    if (mwcPlacement) {
      const { currentValue } = mwcPlacement;
      this.dropDownPosition = currentValue as MwcSelectPlacementType;
      const listOfPlacement = ['bottomLeft', 'topLeft', 'bottomRight', 'topRight'];
      if (currentValue && listOfPlacement.includes(currentValue)) {
        this.positions = [POSITION_MAP[currentValue as POSITION_TYPE]];
      } else {
        this.positions = listOfPlacement.map(e => POSITION_MAP[e as POSITION_TYPE]);
      }
    }
  }

  ngOnInit(): void {
    this.mwcFormStatusService?.formStatusChanges
      .pipe(
        distinctUntilChanged((pre, cur) => {
          return pre.status === cur.status && pre.hasFeedback === cur.hasFeedback;
        }),
        withLatestFrom(this.mwcFormNoStatusService ? this.mwcFormNoStatusService.noFormStatus : observableOf(false)),
        map(([{ status, hasFeedback }, noStatus]) => ({ status: noStatus ? '' : status, hasFeedback })),
        takeUntil(this.destroy$)
      )
      .subscribe(({ status, hasFeedback }) => {
        this.setStatusStyles(status, hasFeedback);
      });

    this.focusMonitor
      .monitor(this.host, true)
      .pipe(takeUntil(this.destroy$))
      .subscribe(focusOrigin => {
        if (!focusOrigin) {
          this.focused = false;
          this.cdr.markForCheck();
          this.mwcBlur.emit();
          Promise.resolve().then(() => {
            this.onTouched();
          });
        } else {
          this.focused = true;
          this.cdr.markForCheck();
          this.mwcFocus.emit();
        }
      });
    combineLatest([this.listOfValue$, this.listOfTemplateItem$])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([listOfSelectedValue, listOfTemplateItem]) => {
        const listOfTagItem = listOfSelectedValue
          .filter(() => this.mwcMode === 'tags')
          .filter(value => listOfTemplateItem.findIndex(o => this.compareWith(o.mwcValue, value)) === -1)
          .map(
            value => this.listOfTopItem.find(o => this.compareWith(o.mwcValue, value)) || this.generateTagItem(value)
          );
        this.listOfTagAndTemplateItem = [...listOfTemplateItem, ...listOfTagItem];
        this.listOfTopItem = this.listOfValue
          .map(
            v =>
              [...this.listOfTagAndTemplateItem, ...this.listOfTopItem].find(item =>
                this.compareWith(v, item.mwcValue)
              )!
          )
          .filter(item => !!item);
        this.updateListOfContainerItem();
      });

    this.directionality.change?.pipe(takeUntil(this.destroy$)).subscribe((direction: Direction) => {
      this.dir = direction;
      this.cdr.detectChanges();
    });

    this.mwcConfigService
      .getConfigChangeEventForComponent('select')
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.cdr.markForCheck();
      });

    this.dir = this.directionality.value;

    this.ngZone.runOutsideAngular(() =>
      fromEvent(this.host.nativeElement, 'click')
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          if ((this.mwcOpen && this.mwcShowSearch) || this.mwcDisabled) {
            return;
          }

          this.ngZone.run(() => this.setOpenState(!this.mwcOpen));
        })
    );

    // Caretaker note: we could've added this listener within the template `(overlayKeydown)="..."`,
    // but with this approach, it'll run change detection on each keyboard click, and also it'll run
    // `markForCheck()` internally, which means the whole component tree (starting from the root and
    // going down to the select component) will be re-checked and updated (if needed).
    // This is safe to do that manually since `setOpenState()` calls `markForCheck()` if needed.
    this.cdkConnectedOverlay.overlayKeydown.pipe(takeUntil(this.destroy$)).subscribe(event => {
      if (event.keyCode === ESCAPE) {
        this.setOpenState(false);
      }
    });
  }

  ngAfterContentInit(): void {
    if (!this.isReactiveDriven) {
      merge(this.listOfMwcOptionGroupComponent.changes, this.listOfMwcOptionComponent.changes)
        .pipe(
          startWith(true),
          switchMap(() =>
            merge(
              ...[
                this.listOfMwcOptionComponent.changes,
                this.listOfMwcOptionGroupComponent.changes,
                ...this.listOfMwcOptionComponent.map(option => option.changes),
                ...this.listOfMwcOptionGroupComponent.map(option => option.changes)
              ]
            ).pipe(startWith(true))
          ),
          takeUntil(this.destroy$)
        )
        .subscribe(() => {
          const listOfOptionInterface = this.listOfMwcOptionComponent.toArray().map(item => {
            const { template, mwcLabel, mwcValue, mwcDisabled, mwcHide, mwcCustomContent, groupLabel } = item;
            return {
              template,
              mwcLabel,
              mwcValue,
              mwcDisabled,
              mwcHide,
              mwcCustomContent,
              groupLabel,
              type: 'item',
              key: mwcValue
            };
          });
          this.listOfTemplateItem$.next(listOfOptionInterface);
          this.cdr.markForCheck();
        });
    }
  }

  ngOnDestroy(): void {
    cancelRequestAnimationFrame(this.requestId);
    this.focusMonitor.stopMonitoring(this.host);
  }

  private setStatusStyles(status: MwcValidateStatus, hasFeedback: boolean): void {
    this.status = status;
    this.hasFeedback = hasFeedback;
    this.cdr.markForCheck();
    // render status if mwcStatus is set
    this.statusCls = getStatusClassNames(this.prefixCls, status, hasFeedback);
    Object.keys(this.statusCls).forEach(status => {
      if (this.statusCls[status]) {
        this.renderer.addClass(this.host.nativeElement, status);
      } else {
        this.renderer.removeClass(this.host.nativeElement, status);
      }
    });
  }
}
